// Hook para el inbox del kine (MensajesKine.tsx). Lista de conversaciones
// (1 por paciente) con last_message + unread_count + last_message_at.
//
// Sub-decisión: derivación en cliente. Tradeoff vs RPC en DB:
//   - DB: 1 query optimizada con DISTINCT ON (paciente_id) por last + count.
//   - Cliente: 2 queries (pacientes + todos los mensajes del kine) + map.
//
// Para piloto con N <= 30 pacientes y M <= 200 mensajes total, cliente es
// suficiente y NO requiere mantener otra RPC. Si llega a producción con
// volumen, migrar a RPC `inbox_conversaciones_kine()` (rule of thumb:
// cuando 95% del payload del fetch sea ignorado por el dedup).
//
// Polling 15s + window focus para inbox refresh (mismo pattern que
// useMensajesConversacion).

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../client";
import { useCurrentKineId } from "@/app/components/auth/AuthProvider";
import type { MensajeRow, MensajeSender } from "./useMensajesConversacion";

export interface ConversacionInbox {
  pacienteId: string;
  pacienteNombre: string;
  pacienteApellido: string;
  /** Último mensaje de la conversación (cualquier sender). null si no
   *  hay mensajes en la conversación. Por simplicidad de la query, igual
   *  incluimos conversaciones sin mensajes (al menos por ahora — el caller
   *  decide si filtrar). */
  lastMessageContent: string | null;
  lastMessageAt: string | null;
  lastMessageSender: MensajeSender | null;
  /** Mensajes del OTRO sender sin leer (read_at IS NULL).
   *  Para el inbox del kine: mensajes del paciente sin leer. */
  unreadCount: number;
}

interface State {
  data: ConversacionInbox[] | null;
  error: string | null;
  loading: boolean;
}

export interface UseConversacionesKineReturn extends State {
  refetch: () => Promise<void>;
}

interface PacienteRow {
  id: string;
  nombre: string;
  apellido: string;
}

const POLL_MS = 15_000;

export function useConversacionesKine(): UseConversacionesKineReturn {
  const kineId = useCurrentKineId();
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: true,
  });

  const fetchAll = useCallback(
    async (signal?: { cancelled: boolean }) => {
      if (!kineId) return;
      const [pacientesRes, mensajesRes] = await Promise.all([
        supabase
          .from("pacientes")
          .select("id, nombre, apellido")
          .eq("kine_id", kineId)
          .returns<PacienteRow[]>(),
        // Todos los mensajes del kine — RLS filtra automáticamente a
        // los de sus pacientes. Ordenados desc para derivar last per
        // paciente con un map simple.
        supabase
          .from("mensajes")
          .select("id, paciente_id, sender, content, created_at, read_at")
          .order("created_at", { ascending: false })
          .returns<MensajeRow[]>(),
      ]);

      if (signal?.cancelled) return;

      const error = pacientesRes.error || mensajesRes.error;
      if (error) {
        console.warn("[useConversacionesKine] error", error);
        setState({ data: null, error: error.message, loading: false });
        return;
      }

      const pacientes = pacientesRes.data ?? [];
      const mensajes = mensajesRes.data ?? [];

      // Map<pacienteId, lastMsg> — el primero del array desc es el last.
      const lastByPaciente = new Map<string, MensajeRow>();
      // Map<pacienteId, unreadCount> — mensajes del paciente sin leer.
      const unreadByPaciente = new Map<string, number>();
      for (const m of mensajes) {
        if (!lastByPaciente.has(m.paciente_id)) {
          lastByPaciente.set(m.paciente_id, m);
        }
        if (m.sender === "paciente" && m.read_at === null) {
          unreadByPaciente.set(
            m.paciente_id,
            (unreadByPaciente.get(m.paciente_id) ?? 0) + 1,
          );
        }
      }

      // Lista de pacientes con CONVERSACIÓN — al menos 1 mensaje.
      // (Pacientes sin mensajes no aparecen en el inbox. Si Ale quiere
      // verlos igual con "Sin mensajes — Iniciá la conversación", lo
      // cambiamos a no-filter aquí.)
      const conversaciones: ConversacionInbox[] = pacientes
        .filter((p) => lastByPaciente.has(p.id))
        .map((p) => {
          const last = lastByPaciente.get(p.id)!;
          return {
            pacienteId: p.id,
            pacienteNombre: p.nombre,
            pacienteApellido: p.apellido,
            lastMessageContent: last.content,
            lastMessageAt: last.created_at,
            lastMessageSender: last.sender,
            unreadCount: unreadByPaciente.get(p.id) ?? 0,
          };
        })
        .sort((a, b) => {
          // Ordenar por lastMessageAt desc (más reciente primero).
          const aAt = a.lastMessageAt ?? "";
          const bAt = b.lastMessageAt ?? "";
          return bAt.localeCompare(aAt);
        });

      setState({ data: conversaciones, error: null, loading: false });
    },
    [kineId],
  );

  useEffect(() => {
    if (!kineId) return;
    const signal = { cancelled: false };
    setState((s) => ({ ...s, loading: true }));
    void fetchAll(signal);

    const intervalId = setInterval(() => {
      void fetchAll(signal);
    }, POLL_MS);

    const onFocus = () => {
      void fetchAll(signal);
    };
    window.addEventListener("focus", onFocus);

    return () => {
      signal.cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [kineId, fetchAll]);

  const refetch = useCallback(async () => {
    if (!kineId) return;
    await fetchAll();
  }, [kineId, fetchAll]);

  return { ...state, refetch };
}
